import { useCallback, useReducer, useRef } from "react";
import type { Session } from "./auth.ts";
import { initialState, reducer } from "./chatState.ts";
import { readEvents } from "./stream.ts";

const ERRORS: Record<number, string> = {
  401: "Din inloggning har gått ut. Ladda om sidan och logga in igen.",
  403: "Du saknar behörighet till agenten. Be om att bli tillagd i gruppen för Salesm8-agenten.",
  429: "Lugnt i båten – för många frågor på kort tid. Vänta en minut och försök igen.",
};

const CONSENT_REQUIRED =
  "Salesm8-appen saknar administratörsgodkännande för att anropa agenten. " +
  "Be en Entra-administratör att ge admin consent för appen itm8-salesm8-agent-web.";

/**
 * A tool's own rejection reason (HTTP 422 from the BFF), missing admin consent (403
 * consent_required), or a fixed message per status.
 */
export async function errorMessage(response: Response): Promise<string> {
  if (response.status === 422 || response.status === 403) {
    try {
      const body = (await response.json()) as { error?: string; message?: unknown };
      if (body.error === "tool_error" && typeof body.message === "string" && body.message) {
        return `Ändringen gick inte igenom: ${body.message}`;
      }
      if (body.error === "consent_required") return CONSENT_REQUIRED;
    } catch {
      // Fall through to the generic message.
    }
  }
  return ERRORS[response.status] ?? "Agenten svarar inte just nu. Försök igen om en stund.";
}

export function useChat(session: Session) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const abortRef = useRef<AbortController | null>(null);

  const run = useCallback(
    async (body: Record<string, unknown>) => {
      const abort = new AbortController();
      abortRef.current = abort;
      dispatch({ type: "start", id: crypto.randomUUID() });
      try {
        const token = await session.getToken();
        const response = await fetch("/api/chat", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(body),
          signal: abort.signal,
        });
        if (!response.ok) {
          dispatch({ type: "fail", message: await errorMessage(response) });
          return;
        }
        for await (const event of readEvents(response, abort.signal)) dispatch({ type: "event", event });
        if (stateRef.current.busy) dispatch({ type: "event", event: { event: "response.completed", data: {} } });
      } catch {
        dispatch({ type: "fail", message: abort.signal.aborted ? "Avbrutet." : "Anslutningen bröts. Försök igen." });
      }
    },
    [session],
  );

  const send = useCallback(
    (message: string) => {
      const { previousResponseId, sessionId } = stateRef.current;
      dispatch({ type: "user", id: crypto.randomUUID(), text: message });
      return run({ message, previousResponseId, sessionId });
    },
    [run],
  );

  const decide = useCallback(
    (approvalRequestId: string, approve: boolean) => {
      const { previousResponseId, sessionId } = stateRef.current;
      dispatch({ type: "decision", approvalId: approvalRequestId, decision: approve ? "approved" : "rejected" });
      return run({ approval: { approvalRequestId, approve }, previousResponseId, sessionId });
    },
    [run],
  );

  /** After the user signed in to a tool, ask again so the agent retries the call. */
  const resume = useCallback(() => {
    const { lastMessage, previousResponseId, sessionId } = stateRef.current;
    if (lastMessage) return run({ message: lastMessage, previousResponseId, sessionId });
  }, [run]);

  const stop = useCallback(() => abortRef.current?.abort(), []);
  const reset = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "reset" });
  }, []);

  return { state, send, decide, resume, stop, reset };
}
